import { useCallback, useEffect, useMemo } from 'react';

import { useQuery } from '../../../contexts/RouterContext';
import type { ReportFlow, ReportsV3QueryParams } from '../types';

const flowsSet = new Set<ReportFlow>(['standard', 'custom']);

/**
 * This hook helps to manage the report "flow" (standard vs custom).
 * This is stored in the URL query params, and is used to determine which report options are shown.
 * If there is no flow set (or if its invalid), the effect here will set it
 */
export const useReportFlow = (): readonly [ReportFlow, (flow: ReportFlow) => void] => {
  const [query, setQuery] = useQuery<ReportsV3QueryParams>();

  useEffect(() => {
    if (!query.flow || !flowsSet.has(query.flow)) {
      setQuery({ flow: 'standard' });
    }
  }, [query, setQuery]);

  const flow: ReportFlow = useMemo(() => {
    if (!query.flow || !flowsSet.has(query.flow)) {
      return 'standard';
    }

    return query.flow as ReportFlow;
  }, [query]);

  const setFlow = useCallback((newFlow: ReportFlow) => {
    setQuery({ flow: newFlow });
  }, [setQuery]);

  return [
    flow,
    setFlow,
  ];
};
